/** 認証コンテキスト（機能設計書 §5.1 / UI/UX設計書 §8 ロール分岐） */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from "react";
import { api, setSessionExpiredHandler, tokens } from "./api";
import { PUBLIC_MODE } from "./publicMode";
import type { Cart, LoginResponse, UserMe } from "./types";

interface AuthState {
  user: UserMe | null;
  loading: boolean;
  cartCount: number;
  unreadCount: number;
  login: (email: string, password: string) => Promise<UserMe>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  refreshBadges: () => Promise<void>;
}

const AuthContext = createContext<AuthState>(null as unknown as AuthState);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshBadges = useCallback(async () => {
    if (!tokens.access) { setCartCount(0); setUnreadCount(0); return; }
    try {
      const [cart, notices] = await Promise.all([
        api.get<Cart>("/cart"),
        api.get<{ unread_count: number }>("/notices", { limit: 1 }),
      ]);
      setCartCount(cart.items.reduce((n, i) => n + i.quantity, 0));
      setUnreadCount(notices.unread_count);
    } catch {
      /* バッジは失敗しても業務に影響しない */
    }
  }, []);

  /** 一般公開モードでは、来訪者ごとにゲストを発行してそのまま入場できるようにする */
  const enterAsGuest = useCallback(async () => {
    try {
      const res = await api.post<LoginResponse>("/auth/guest", {});
      tokens.set(res);
      setUser(res.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const reload = useCallback(async () => {
    if (!tokens.access) {
      if (PUBLIC_MODE) { await enterAsGuest(); return; }
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api.get<UserMe>("/users/me");
      setUser(me);
      if (!PUBLIC_MODE) await refreshBadges();
    } catch {
      tokens.clear();
      if (PUBLIC_MODE) { await enterAsGuest(); return; }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [refreshBadges, enterAsGuest]);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      tokens.clear();
      setUser(null);
      // 公開モードではゲストを取り直して、来訪者を締め出さない
      if (PUBLIC_MODE) void enterAsGuest();
    });
    void reload();
  }, [reload, enterAsGuest]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<LoginResponse>("/auth/login", { email, password });
    tokens.set(res);
    setUser(res.user);
    await refreshBadges();
    return res.user;
  }, [refreshBadges]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout", { refresh_token: tokens.refresh }); } catch { /* noop */ }
    tokens.clear();
    setUser(null);
    setCartCount(0);
    setUnreadCount(0);
  }, []);

  const value = useMemo(
    () => ({ user, loading, cartCount, unreadCount, login, logout, reload, refreshBadges }),
    [user, loading, cartCount, unreadCount, login, logout, reload, refreshBadges],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
